import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HeroOpen.css";

/**
 * OPENlift — Hero Premium, section 01.
 * Contenu et visuel alignés sur l'audit commercial/design d'octobre 2026.
 * Les nouvelles clés i18n évitent les anciens textes et pourcentages du Hero.
 * Les props imageSrc/title/subtitle restent acceptées pour compatibilité.
 */
const COPY = {
  fr: {
    eyebrow: "OPENlift · Supervision intelligente des ascenseurs",
    firstLine: "Comprenez les pannes",
    firstAccent: "plus vite.",
    secondLine: "Intervenez plus",
    secondAccent: "efficacement.",
    description:
      "Avec OPENlift, gardez une vision claire de votre parc d'ascenseurs, consultez les informations techniques à distance et préparez vos interventions avec les bonnes données, au bon moment.",
    features: ["Supervision à distance", "Diagnostic facilité", "Parc multimarques"],
    mainCta: "Demander une démonstration",
    secondCta: "Vérifier la compatibilité de mon parc",
    imageAlt: "Vue du tableau de bord OPENlift et de l'application mobile, affichée sur un ordinateur et un smartphone",
    imageNote: "Visuel OPENlift fourni · données affichées à titre d'exemple"
  },
  en: {
    eyebrow: "OPENlift · Intelligent elevator monitoring",
    firstLine: "Understand issues",
    firstAccent: "faster.",
    secondLine: "Act more",
    secondAccent: "effectively.",
    description:
      "With OPENlift, keep a clear view of your elevator fleet, access technical information remotely and prepare your interventions with the right data, at the right time.",
    features: ["Remote monitoring", "Easier diagnostics", "Multi-brand fleets"],
    mainCta: "Request a demonstration",
    secondCta: "Check fleet compatibility",
    imageAlt: "OPENlift dashboard and mobile application shown on a laptop and smartphone",
    imageNote: "OPENlift visual provided · displayed data is illustrative"
  }
};

function ArrowIcon({ diagonal = false }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false">
      {diagonal ? (
        <><path d="M7 17 17 7" /><path d="M8 7h9v9" /></>
      ) : (
        <><path d="M4 12h16" /><path d="m13 5 7 7-7 7" /></>
      )}
    </svg>
  );
}

function FeatureIcon({ index }) {
  const paths = [
    <><path d="M2.5 12s3.4-6.3 9.5-6.3 9.5 6.3 9.5 6.3-3.4 6.3-9.5 6.3S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></>,
    <><path d="M14 7a4 4 0 0 0-5 5l-6 6a2.1 2.1 0 0 0 3 3l6-6a4 4 0 0 0 5-5l-3 3-3-3Z" /></>,
    <><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5M3 17l9 5 9-5" /></>
  ];
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false">
      {paths[index]}
    </svg>
  );
}

export default function HeroOpen({ imageSrc, title, subtitle }) {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = COPY[language] || COPY.fr;

  const translate = (key, fallback) =>
    t(`heroOpen.premium.${key}`, { defaultValue: fallback });

  const features = copy.features.map((fallback, i) =>
    translate(`features.${i}`, fallback)
  );
  const productImage = imageSrc || `${process.env.PUBLIC_URL || ""}/images/OPENliftBack2.png`;

  return (
    <section className="ol-hero-premium" aria-labelledby="ol-hero-premium-title">
      <div className="ol-hero-premium__container">
        <div className="ol-hero-premium__layout">
          <div className="ol-hero-premium__content">
            <p className="ol-hero-premium__eyebrow">
              <span className="ol-hero-premium__eyebrow-rule" aria-hidden="true" />
              {translate("eyebrow", copy.eyebrow)}
            </p>

            <h1 id="ol-hero-premium-title" className="ol-hero-premium__title">
              {title ? title : (
                <>
                  <span className="ol-hero-premium__title-line">
                    {translate("firstLine", copy.firstLine)}{" "}
                    <em>{translate("firstAccent", copy.firstAccent)}</em>
                  </span>
                  <span className="ol-hero-premium__title-line">
                    {translate("secondLine", copy.secondLine)}{" "}
                    <em>{translate("secondAccent", copy.secondAccent)}</em>
                  </span>
                </>
              )}
            </h1>

            <p className="ol-hero-premium__description">
              {subtitle || translate("description", copy.description)}
            </p>

            <ul className="ol-hero-premium__features">
              {features.map((feature, index) => (
                <li key={index}>
                  <FeatureIcon index={index} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="ol-hero-premium__actions">
              <NavLink className="ol-hero-premium__cta ol-hero-premium__cta--primary" to="/demo">
                <span>{translate("mainCta", copy.mainCta)}</span>
                <ArrowIcon />
              </NavLink>
              <a className="ol-hero-premium__cta ol-hero-premium__cta--secondary" href="#ol-compat-title">
                <span>{translate("secondCta", copy.secondCta)}</span>
                <ArrowIcon diagonal />
              </a>
            </div>
          </div>

          <div className="ol-hero-premium__visual">
            <figure className="ol-hero-premium__figure">
              <img
                src={productImage}
                className="ol-hero-premium__image"
                alt={translate("imageAlt", copy.imageAlt)}
                width="1920"
                height="1080"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
